import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Download, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

const BLANK = {
  job_id: null,
  studio_id: "",
  owner_name: "",
  owner_phone: "",
  owner_email: "",
  mailing_address: "",
  brand: "",
  model: "",
  colour: "",
  reg_no: "",
  chassis_no: "",
  panels_coverage: "Full",
  panels_notes: "",
  studio_name: "",
  studio_contact: "",
  crm_ref: "",
  installer_team_name: "",
  installer_team_code: "",
  serial_number: "",
  serial_id: "",
  film_type: "Veloskin PPF",
  area_sqft: "",
  install_date: "",
  invoice_date: "",
  warranty_start_date: today(),
  warranty_years: 5,
  notes: "",
  product_type: "PPF",
  product_name: "",
  warranty_type: "5 Year",
  sunfilm_coverage_areas: [],
};

const PPF_WARRANTY_TYPES = ["5 Year", "7 Year", "8 Year", "10 Year"];
const SUNFILM_WARRANTY_TYPES = ["VLT 70%", "VLT 50%", "Custom VLT"];
const yearsFromType = (t) => parseInt(t, 10) || 5;
const isX250 = (n) => /x[\s-]?250/i.test(n || "");

const AREA_TYPES = ["Front Windshield", "Rear Windshield", "Front Side Windows",
  "Rear Side Windows", "Quarter Glass", "Sunroof", "Full Vehicle Glass"];
const VLT_DEFAULT = {
  "Front Windshield": "70%", "Rear Windshield": "70%",
  "Front Side Windows": "50%", "Rear Side Windows": "50%", "Quarter Glass": "50%",
};
const VLT_MIN = { front: 70, side: 50 };
const vltNum = (v) => parseFloat(String(v).replace("%", "").trim());
const vltWarning = (area, vlt) => {
  const n = vltNum(vlt);
  if (isNaN(n)) return "";
  if ((area === "Front Windshield" || area === "Rear Windshield") && n < VLT_MIN.front)
    return "Below the 70% legal minimum for windshields under India's Central Motor Vehicle Rules — confirm this is intentional before proceeding.";
  if (["Front Side Windows", "Rear Side Windows", "Quarter Glass"].includes(area) && n < VLT_MIN.side)
    return "Below the 50% legal minimum for side/quarter glass under India's Central Motor Vehicle Rules — confirm this is intentional before proceeding.";
  return "";
};

const STD_VLT = ["50%", "70%", "__custom__"];

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const SunfilmCoverage = ({ rows, onChange }) => {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...rows, { area_type: "", vlt_percentage: "", area_sqft: "", _custom: false }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="rounded-lg border border-brand-line p-3" data-testid="sunfilm-coverage">
      <div className="mb-2 flex items-center justify-between">
        <Label className="text-xs font-semibold">Glass coverage areas *</Label>
        <Button type="button" size="sm" variant="outline" data-testid="add-coverage-row" onClick={addRow}>
          + Add area
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="py-2 text-xs text-slate-400" data-testid="coverage-empty">
          Add at least one glass area (each can have its own VLT %).
        </p>
      )}
      <div className="space-y-3">
        {rows.map((r, i) => {
          const warn = vltWarning(r.area_type, r.vlt_percentage);
          const isCustom = r._custom || (r.vlt_percentage && !["50%", "70%"].includes(r.vlt_percentage));
          return (
            <div key={i} className="rounded-md bg-brand-mist/40 p-2" data-testid={`coverage-row-${i}`}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Select
                  value={r.area_type}
                  onValueChange={(v) => update(i, { area_type: v, vlt_percentage: VLT_DEFAULT[v] || "", _custom: false })}
                >
                  <SelectTrigger data-testid={`coverage-area-${i}`}><SelectValue placeholder="Glass area" /></SelectTrigger>
                  <SelectContent>
                    {AREA_TYPES.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Select
                  value={isCustom ? "__custom__" : r.vlt_percentage}
                  onValueChange={(v) => update(i, v === "__custom__" ? { _custom: true, vlt_percentage: "" } : { _custom: false, vlt_percentage: v })}
                >
                  <SelectTrigger data-testid={`coverage-vlt-${i}`}><SelectValue placeholder="VLT %" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="70%">70%</SelectItem>
                    <SelectItem value="50%">50%</SelectItem>
                    <SelectItem value="__custom__">Custom…</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input inputMode="decimal" placeholder="sq.ft. (opt)" value={r.area_sqft}
                    data-testid={`coverage-sqft-${i}`} onChange={(e) => update(i, { area_sqft: e.target.value })} />
                  <Button type="button" size="sm" variant="ghost" data-testid={`remove-coverage-${i}`} onClick={() => removeRow(i)}>✕</Button>
                </div>
              </div>
              {isCustom && (
                <Input className="mt-2" placeholder="Custom VLT e.g. 65%" value={r.vlt_percentage}
                  data-testid={`coverage-custom-vlt-${i}`} onChange={(e) => update(i, { vlt_percentage: e.target.value })} />
              )}
              {warn && (
                <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800" data-testid={`vlt-warning-${i}`}>
                  ⚠ {warn}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Rich Veloskin PPF warranty registration form. `prefill` seeds fields from a job/customer.
export const WarrantyRegister = ({ open, onOpenChange, prefill, studios = [], onDone, lockProduct = null }) => {
  const [form, setForm] = useState(BLANK);
  const [serials, setSerials] = useState([]);
  const [rollSerials, setRollSerials] = useState([]);
  const [jobSerials, setJobSerials] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [products, setProducts] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      const isSf = lockProduct === "Velotint Sunfilm";
      setForm({
        ...BLANK,
        product_type: lockProduct || "PPF",
        film_type: isSf ? "Velotint Nano Ceramic Sunfilm" : "Veloskin PPF",
        warranty_type: isSf ? "Velotint Sunfilm" : BLANK.warranty_type,
        ...(prefill || {}),
      });
      setResult(null);
      api.get("/warranty/products").then(({ data }) => {
        let list = data || [];
        if (lockProduct)
          list = list.filter(
            (p) => (p.product_type === "Velotint Sunfilm") === (lockProduct === "Velotint Sunfilm"));
        setProducts(list);
      }).catch(() => setProducts([]));
    }
  }, [open, prefill, lockProduct]);

  // When a product is chosen, derive its brand card (product_type) and reset the dependent
  // warranty-type dropdown to that brand's first option.
  const onProductChange = (name) => {
    const p = products.find((x) => x.name === name);
    const pt = p?.product_type || "PPF";
    const isSf = pt === "Velotint Sunfilm";
    setForm((f) => ({
      ...f,
      product_name: name,
      product_type: pt,
      film_type: isSf ? "Velotint Nano Ceramic Sunfilm" : "Veloskin PPF",
      warranty_type: isSf ? SUNFILM_WARRANTY_TYPES[0] : isX250(name) ? "8 Year" : PPF_WARRANTY_TYPES[0],
      warranty_years: isSf ? f.warranty_years : isX250(name) ? 8 : 5,
    }));
  };

  const onWarrantyTypeChange = (t) => {
    setForm((f) => ({
      ...f,
      warranty_type: t,
      warranty_years: f.product_type === "Velotint Sunfilm" ? f.warranty_years : yearsFromType(t),
    }));
  };

  const sid = form.studio_id;
  const loadSerials = useCallback(() => {
    if (!sid) return setSerials([]);
    api
      .get("/warranty/serials", { params: { studio_id: sid } })
      .then(({ data }) => setSerials(data))
      .catch(() => setSerials([]));
  }, [sid]);
  useEffect(() => {
    if (open) loadSerials();
  }, [open, loadSerials]);

  const isSunfilm = form.product_type === "Velotint Sunfilm";
  useEffect(() => {
    if (!isSunfilm && isX250(form.product_name) && Number(form.warranty_years) !== 8)
      setForm((f) => ({ ...f, warranty_type: "8 Year", warranty_years: 8 }));
  }, [isSunfilm, form.product_name, form.warranty_years]);

  // Serials already consumed against this job — auto-link them to the warranty.
  const jobId = form.job_id;
  const [wDetails, setWDetails] = useState(null);
  useEffect(() => {
    if (!open || !jobId) return setJobSerials([]);
    api
      .get(`/jobs/${jobId}/consumed-serials`)
      .then(({ data }) => setJobSerials(data || []))
      .catch(() => setJobSerials([]));
  }, [open, jobId]);
  useEffect(() => {
    if (!open || !jobId) return setWDetails(null);
    api
      .get(`/jobs/${jobId}/warranty-details`, { params: lockProduct ? { product_type: lockProduct } : {} })
      .then(({ data }) => {
        setWDetails(data);
        setForm((f) => ({
          ...f,
          owner_email: f.owner_email || data.customer_email || "",
          reg_no: f.reg_no || data.registration_no || "",
          chassis_no: f.chassis_no || data.chassis_vin || "",
          brand: f.brand || data.vehicle_make || "",
          model: f.model || data.vehicle_model || "",
          colour: f.colour || data.vehicle_colour || "",
          area_sqft: f.area_sqft || (data.area_sqft != null ? String(data.area_sqft) : ""),
          product_name: f.product_name || data.product_name || "",
          installer_team_name: f.installer_team_name || data.installer_name || "",
          install_date: f.install_date || (data.installation_date || "").slice(0, 10),
          invoice_date: f.invoice_date || (data.invoice_date || "").slice(0, 10),
        }));
      })
      .catch(() => setWDetails(null));
  }, [open, jobId, lockProduct]);

  const category = isSunfilm ? "Sunfilm" : "PPF";
  const jobSerialsForCat = jobSerials.filter((s) => s.category === category);

  // Merge consumed-on-this-job serials into the pickable list (dedupe, job serials first).
  const serialOptions = (() => {
    const map = new Map();
    jobSerialsForCat.forEach((s) => map.set(s.id, s));
    rollSerials.forEach((s) => { if (!map.has(s.id)) map.set(s.id, s); });
    return [...map.values()];
  })();

  // Auto-select the consumed serial (first, if multiple) unless the user already picked one of them.
  useEffect(() => {
    if (!jobSerialsForCat.length) return;
    setForm((f) => {
      if (f.serial_id && jobSerialsForCat.some((s) => s.id === f.serial_id)) return f;
      const first = jobSerialsForCat[0];
      return { ...f, serial_id: first.id, serial_number: first.serial };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobSerials, isSunfilm]);

  useEffect(() => {
    if (!open || !sid) return setRollSerials([]);
    const category = isSunfilm ? "Sunfilm" : "PPF";
    api
      .get(`/roll-serials?status=received&has_balance=true&studio_id=${sid}&category=${category}`)
      .then(({ data }) => setRollSerials(data || []))
      .catch(() => setRollSerials([]));
  }, [open, sid, isSunfilm]);

  const submit = async () => {
    if (!form.owner_name.trim()) return toast.error("Owner name is required");
    if (!form.studio_name.trim() && !form.studio_id && !form.job_id)
      return toast.error("Enter the installing detailer / studio");
    if (!isSunfilm && !form.product_name) return toast.error("Select a product");
    if (isSunfilm) {
      if (!form.sunfilm_coverage_areas.length)
        return toast.error("Add at least one Sunfilm coverage area");
      if (form.sunfilm_coverage_areas.some((r) => !r.area_type || !String(r.vlt_percentage).trim()))
        return toast.error("Every coverage row needs a glass area and a VLT %");
    }
    setBusy(true);
    try {
      // All rolls consumed on this job go on the certificate; free-entry has no job so it
      // falls back to the single serial the CRM picked/typed.
      const rolls = jobSerialsForCat.length
        ? jobSerialsForCat.map((s) => ({ serial_number: s.serial, roll_size: "" }))
        : ((form.serial_number || "").trim()
            ? [{ serial_number: form.serial_number.trim(), roll_size: "" }]
            : []);
      const payload = {
        ...form,
        rolls,
        area_sqft: form.area_sqft === "" ? null : Number(form.area_sqft),
        warranty_years: Number(form.warranty_years || 5),
        sunfilm_coverage_areas: isSunfilm
          ? form.sunfilm_coverage_areas.map((r) => {
              let vlt = String(r.vlt_percentage).trim();
              if (vlt && !vlt.endsWith("%")) vlt = `${vlt}%`;
              return {
                area_type: r.area_type, vlt_percentage: vlt,
                area_sqft: r.area_sqft === "" || r.area_sqft == null ? null : Number(r.area_sqft),
              };
            })
          : [],
      };
      const { data } = await api.post("/warranty/register", payload);
      setResult(data);
      toast.success(`Certificate ${data.certificate_no} generated`);
      onDone && onDone(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    if (result?.id) window.open(`${API}/warranties/${result.id}/certificate`, "_blank");
  };

  const sendToCustomer = async () => {
    if (!result?.id) return;
    setSending(true);
    try {
      const { data } = await api.post(`/warranties/${result.id}/send`);
      setResult((r) => ({ ...r, send_status: "sent", email: data.email,
                          whatsapp_link: data.whatsapp_link || r.whatsapp_link }));
      if (data.email?.sent)
        toast.success(`Sent to ${Array.isArray(data.email.to) ? data.email.to.join(", ") : data.email.to}`);
      else toast.warning(`Marked sent — email not delivered (${data.email?.reason || "no email on file"})`);
      onDone && onDone(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sent = result?.send_status === "sent";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="warranty-register-dialog"
      >
        <DialogHeader>
          <DialogTitle>Register {isSunfilm ? "Velotint Sunfilm" : "Veloskin PPF"} warranty</DialogTitle>
          <DialogDescription>
            Step 1 — generates the 5-Year {isSunfilm ? "Velotint Sunfilm" : "Protection"} Certificate and
            stores it against the job. Review the PDF, then use "Send to Customer" to dispatch it.
          </DialogDescription>
        </DialogHeader>

        {!result && wDetails && (
          <div
            className="rounded-lg border border-brand-line bg-slate-50/70 p-3 text-xs"
            data-testid="warranty-register-details-onfile"
          >
            <p className="mb-1.5 font-semibold uppercase tracking-wide text-slate-500">
              Warranty details on file (from Job Detail)
            </p>
            <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
              <span><span className="text-slate-400">Email:</span> {wDetails.customer_email || "—"}</span>
              <span><span className="text-slate-400">Chassis/VIN:</span> {wDetails.chassis_vin || "—"}</span>
              <span><span className="text-slate-400">Reg no:</span> {wDetails.registration_no || "—"}</span>
              <span><span className="text-slate-400">Serials:</span> {(wDetails.serials || []).join(", ") || "—"}</span>
              {wDetails.warranty_notes && (
                <span className="sm:col-span-2"><span className="text-slate-400">Notes:</span> {wDetails.warranty_notes}</span>
              )}
            </div>
          </div>
        )}

        {result ? (
          <div className="space-y-4" data-testid="warranty-register-result">
            <div className="rounded-lg border border-brand-line bg-brand-tint/50 p-4">
              <p className="flex flex-wrap items-center gap-2 font-display text-lg font-semibold text-brand-ink">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Certificate {result.certificate_no}
                <span
                  data-testid="warranty-result-status"
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    sent ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"
                  }`}
                >
                  {sent ? "Approved · Sent" : "Registered"}
                </span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {result.owner_name} · expires {result.expiry_date}
              </p>
              <p className="mt-2 text-sm">
                {sent ? (
                  result.email?.sent ? (
                    <span className="font-medium text-emerald-700">
                      Emailed to {Array.isArray(result.email.to) ? result.email.to.join(", ") : result.email.to}
                    </span>
                  ) : (
                    <span className="font-medium text-amber-700">
                      Marked sent — email not delivered ({result.email?.reason || "no email on file"})
                    </span>
                  )
                ) : (
                  <span className="font-medium text-slate-600">
                    Certificate generated and stored — not sent yet. Review the PDF, then send it to the customer.
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" data-testid="warranty-download-btn" onClick={downloadPdf}>
                <Download className="mr-2 h-4 w-4" /> View / download certificate
              </Button>
              {!sent && (
                <Button
                  data-testid="warranty-send-btn"
                  onClick={sendToCustomer}
                  disabled={sending}
                >
                  <Send className="mr-2 h-4 w-4" /> {sending ? "Sending…" : "Send to Customer"}
                </Button>
              )}
              {sent && (
                <a
                  href={result.whatsapp_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="warranty-whatsapp-btn"
                >
                  <Button variant="outline" className="border-emerald-500 text-emerald-700">
                    <MessageCircle className="mr-2 h-4 w-4" /> Share on WhatsApp
                  </Button>
                </a>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Owner name *">
                <Input data-testid="wr-owner-name" value={form.owner_name}
                  onChange={(e) => set("owner_name", e.target.value)} />
              </Field>
              <Field label="Phone number">
                <Input data-testid="wr-owner-phone" value={form.owner_phone}
                  onChange={(e) => set("owner_phone", e.target.value)} />
              </Field>
              <Field label="Email (certificate is sent here)">
                <Input data-testid="wr-owner-email" value={form.owner_email}
                  onChange={(e) => set("owner_email", e.target.value)} />
              </Field>
              <Field label="Mailing address">
                <Input value={form.mailing_address}
                  onChange={(e) => set("mailing_address", e.target.value)} />
              </Field>
            </div>

            {!form.job_id && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Installing detailer / studio *">
                  <Input
                    data-testid="wr-detailer-name"
                    value={form.studio_name}
                    placeholder="Type any detailer / studio name"
                    onChange={(e) => set("studio_name", e.target.value)}
                  />
                </Field>
                {studios.length > 0 && (
                  <Field label="Or pick an accredited studio">
                    <Select
                      value={form.studio_id}
                      onValueChange={(v) => {
                        const s = studios.find((x) => x.id === v);
                        setForm((f) => ({ ...f, studio_id: v, studio_name: s?.name || f.studio_name }));
                      }}
                    >
                      <SelectTrigger data-testid="wr-studio-select">
                        <SelectValue placeholder="Accredited studio (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {studios.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Car brand">
                <Input data-testid="wr-brand" value={form.brand}
                  onChange={(e) => set("brand", e.target.value)} />
              </Field>
              <Field label="Model">
                <Input value={form.model} onChange={(e) => set("model", e.target.value)} />
              </Field>
              <Field label="Colour">
                <Input value={form.colour} onChange={(e) => set("colour", e.target.value)} />
              </Field>
              <Field label="Registration no.">
                <Input data-testid="wr-reg-no" value={form.reg_no}
                  onChange={(e) => set("reg_no", e.target.value)} />
              </Field>
              <Field label="Chassis no.">
                <Input value={form.chassis_no} onChange={(e) => set("chassis_no", e.target.value)} />
              </Field>
              {!isSunfilm && (
                <Field label="Area covered (sq. ft.)">
                  <Input inputMode="decimal" value={form.area_sqft}
                    onChange={(e) => set("area_sqft", e.target.value)} />
                </Field>
              )}
            </div>

            {!isSunfilm && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Product *">
                  <Select value={form.product_name} onValueChange={onProductChange}>
                    <SelectTrigger data-testid="wr-product">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.name}>
                          {p.name} · {p.product_type === "Velotint Sunfilm" ? "Velotint" : "Veloskin"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Warranty type *">
                  <Select value={form.warranty_type} onValueChange={onWarrantyTypeChange}>
                    <SelectTrigger data-testid="wr-warranty-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PPF_WARRANTY_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}
            <p className="-mt-1 text-xs text-slate-500" data-testid="wr-card-hint">
              {isSunfilm
                ? "A Velotint Sunfilm warranty card will be generated."
                : "A Veloskin PPF warranty card will be generated."}
            </p>

            {isSunfilm ? (
              <SunfilmCoverage
                rows={form.sunfilm_coverage_areas}
                onChange={(rows) => set("sunfilm_coverage_areas", rows)}
              />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Panels covered">
                  <Select value={form.panels_coverage} onValueChange={(v) => set("panels_coverage", v)}>
                    <SelectTrigger data-testid="wr-panels"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full">Full body</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {form.panels_coverage === "Partial" && (
                  <Field label="Partial coverage notes">
                    <Input data-testid="wr-panels-notes" value={form.panels_notes}
                      onChange={(e) => set("panels_notes", e.target.value)} />
                  </Field>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Studio contact">
                <Input value={form.studio_contact}
                  onChange={(e) => set("studio_contact", e.target.value)} />
              </Field>
              <Field label="CRM / job reference">
                <Input value={form.crm_ref} onChange={(e) => set("crm_ref", e.target.value)} />
              </Field>
              <Field label="Installer team name">
                <Input value={form.installer_team_name}
                  onChange={(e) => set("installer_team_name", e.target.value)} />
              </Field>
              <Field label="Installer team code">
                <Input value={form.installer_team_code}
                  onChange={(e) => set("installer_team_code", e.target.value)} />
              </Field>
            </div>

            <Field label="Roll serial number (received at studio, balance remaining)">
              {jobSerialsForCat.length > 0 && (
                <div
                  className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 p-2"
                  data-testid="wr-consumed-serials"
                >
                  <p className="text-xs font-medium text-emerald-800">
                    {jobSerialsForCat.length === 1
                      ? "Auto-linked from the material consumption logged on this job:"
                      : "This job consumed multiple rolls — all of these serial numbers will be printed on the certificate:"}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {jobSerialsForCat.map((s) => {
                      const on = form.serial_id === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`wr-consumed-serial-${s.serial}`}
                          onClick={() =>
                            setForm((f) => ({ ...f, serial_id: s.id, serial_number: s.serial }))
                          }
                          className={`rounded-full border px-3 py-1 font-mono text-xs transition-colors ${
                            on
                              ? "border-emerald-600 bg-emerald-600 text-white"
                              : "border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
                          }`}
                        >
                          {s.serial}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {serialOptions.length > 0 ? (
                <Select
                  value={form.serial_id || ""}
                  onValueChange={(v) => {
                    const r = serialOptions.find((x) => x.id === v);
                    setForm((f) => ({ ...f, serial_id: v, serial_number: r ? r.serial : f.serial_number }));
                  }}
                >
                  <SelectTrigger data-testid="wr-serial-select">
                    <SelectValue placeholder="Select roll serial" />
                  </SelectTrigger>
                  <SelectContent>
                    {serialOptions.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`wr-serial-opt-${s.serial}`}>
                        {s.serial} — {Math.round(s.balance_sqft)} sqft remaining
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid="wr-serial-input" placeholder="Serial number"
                  value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} />
              )}
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <Field label="Film type">
                <Input value={form.film_type} onChange={(e) => set("film_type", e.target.value)} />
              </Field>
              <Field label="Installation date">
                <Input type="date" value={form.install_date}
                  onChange={(e) => set("install_date", e.target.value)} />
              </Field>
              <Field label="Invoice date">
                <Input type="date" value={form.invoice_date}
                  onChange={(e) => set("invoice_date", e.target.value)} />
              </Field>
              <Field label="Warranty years">
                <Input inputMode="numeric" value={form.warranty_years} data-testid="warranty-years-input"
                  disabled={!isSunfilm && isX250(form.product_name)}
                  onChange={(e) => set("warranty_years", e.target.value)} />
                {!isSunfilm && isX250(form.product_name) && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="x250-years-note">
                    Veloskin X250 carries an 8-year warranty.</p>
                )}
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Warranty start date">
                <Input type="date" data-testid="wr-start-date" value={form.warranty_start_date}
                  onChange={(e) => set("warranty_start_date", e.target.value)} />
              </Field>
              <Field label="Additional notes">
                <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </Field>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="warranty-register-submit" disabled={busy} onClick={submit}>
                <ShieldCheck className="mr-2 h-4 w-4" /> Register
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Small action group for the warranty dashboard rows.
export const WarrantyRowActions = ({ warranty, onChanged }) => {
  const { user } = useAuth();
  const canSend = can(user, "warranty:write");
  const [busy, setBusy] = useState(false);
  const sent = warranty.send_status === "sent" || (!warranty.send_status && !!warranty.emailed_at);
  const dispatch = async (endpoint, sentMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/warranties/${warranty.id}/${endpoint}`);
      if (data.email?.sent)
        toast.success(`${sentMsg} ${Array.isArray(data.email.to) ? data.email.to.join(", ") : data.email.to}`);
      else toast.warning(`Marked sent — email not delivered (${data.email?.reason || "no email on file"})`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        size="sm"
        variant="outline"
        data-testid={`warranty-pdf-${warranty.id}`}
        title="View / download certificate"
        onClick={() => window.open(`${API}/warranties/${warranty.id}/certificate`, "_blank")}
      >
        <Download className="h-3.5 w-3.5" />
      </Button>
      {canSend && !sent && (
        <Button
          size="sm"
          disabled={busy}
          data-testid={`warranty-send-${warranty.id}`}
          onClick={() => dispatch("send", "Sent to")}
        >
          <Send className="mr-1.5 h-3.5 w-3.5" /> Send to Customer
        </Button>
      )}
      {canSend && sent && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          data-testid={`warranty-resend-${warranty.id}`}
          title="Re-send to customer"
          onClick={() => dispatch("resend", "Re-sent to")}
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
};
